/**
 * @file useAutosavePart.ts
 * @description Автосохранение части черновика «вида сайта» (phase19.16 Р1;
 * uiux-design-r1 §0.1 «признак сохранения», §1 «Черновик не сохранился»;
 * контракт §0 — оптимистическая блокировка по частям).
 *
 * Как работает:
 *  - правка меняет локальное значение сразу (поле не «прыгает»), сохранение
 *    уходит через `delay` после последней правки (debounce);
 *  - каждое сохранение несёт `expectedVersion`; ответ даёт новую версию;
 *  - правки, сделанные во время сохранения, уходят следующим запросом —
 *    ответ сервера не затирает то, что человек успел напечатать;
 *  - 409 `site_view_version_conflict` — статус `conflict` с тем, кто правил;
 *    `reload` перечитывает часть с сервера и отбрасывает свои правки;
 *  - иная ошибка — статус `error`, правки остаются в окне, `retry` повторяет.
 * Серверное значение подхватывается, пока своих несохранённых правок нет
 * (после «Отменить изменения» или чужой публикации).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { parseVersionConflict, siteViewErrorText, type SiteVersionConflict } from "../../lib/site-view/errors";
import type { SitePartState } from "../../lib/site-view/types";

export type AutosaveStatus = "idle" | "pending" | "saving" | "saved" | "error" | "conflict";

/** Итог сохранения: новое состояние части и значение с сервера. */
export interface AutosaveResult<V> {
  part: SitePartState;
  value: V;
}

export interface AutosaveOptions<V> {
  /** Значение части с сервера (из черновика). */
  serverValue: V | null;
  /** Версия части с сервера. */
  serverVersion: number;
  /** Сохранить: значение, ожидаемая версия, ключи, изменённые с прошлого сохранения. */
  save: (value: V, expectedVersion: number, changedKeys: ReadonlySet<string>) => Promise<AutosaveResult<V>>;
  /** Перечитать часть с сервера (после конфликта). */
  load: () => Promise<AutosaveResult<V>>;
  /** Сообщить каркасу о новой версии части и значении. */
  onSaved?: (result: AutosaveResult<V>) => void;
  /** Задержка перед сохранением, мс. */
  delay?: number;
}

export interface AutosaveApi<V> {
  value: V | null;
  /** Правка: новое значение и ключи верхнего уровня, которые изменились. */
  change: (next: V, keys: readonly string[]) => void;
  status: AutosaveStatus;
  savedAt: Date | null;
  error: string | null;
  conflict: SiteVersionConflict | null;
  /** Есть правки, которых нет на сервере. */
  dirty: boolean;
  retry: () => void;
  reload: () => Promise<void>;
  /**
   * Дождаться, пока правки дойдут до сервера (отложенные — отправить сразу).
   * Итог — версия части на сервере; `null` — не сохранилось (ошибка или
   * конфликт). Нужен перед действиями с серверным черновиком целиком
   * (применить или сохранить дизайн).
   */
  settle: () => Promise<number | null>;
}

export function useAutosavePart<V>({
  serverValue,
  serverVersion,
  save,
  load,
  onSaved,
  delay = 800,
}: AutosaveOptions<V>): AutosaveApi<V> {
  const [value, setValue] = useState<V | null>(serverValue);
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<SiteVersionConflict | null>(null);
  const valueRef = useRef<V | null>(serverValue);
  const versionRef = useRef(serverVersion);
  const pendingKeys = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(false);
  const blocked = useRef(false);
  // Свежие колбэки без перезапуска эффектов.
  const io = useRef({ save, load, onSaved });
  io.current = { save, load, onSaved };

  // Серверное значение — пока своих правок нет.
  useEffect(() => {
    if (pendingKeys.current.size > 0 || inFlight.current) return;
    valueRef.current = serverValue;
    versionRef.current = serverVersion;
    setValue(serverValue);
  }, [serverValue, serverVersion]);

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (inFlight.current || blocked.current || pendingKeys.current.size === 0 || valueRef.current === null) return;
    inFlight.current = true;
    const keys = new Set(pendingKeys.current);
    pendingKeys.current.clear();
    const sent = valueRef.current;
    setStatus("saving");
    let failed = false;
    lastFailed.current = false;
    try {
      const result = await io.current.save(sent, versionRef.current, keys);
      versionRef.current = result.part.version;
      io.current.onSaved?.(result);
      setSavedAt(new Date());
      setError(null);
      if (pendingKeys.current.size === 0) {
        // Пока шёл запрос, правок не было: показываем то, что сохранил сервер.
        valueRef.current = result.value;
        setValue(result.value);
        setStatus("saved");
      }
    } catch (err) {
      failed = true;
      lastFailed.current = true;
      for (const key of keys) pendingKeys.current.add(key);
      const parsed = parseVersionConflict(err);
      if (parsed) {
        blocked.current = true;
        setConflict(parsed);
        setStatus("conflict");
      } else {
        setError(siteViewErrorText(err, "Изменения не сохранились — проверьте связь."));
        setStatus("error");
      }
    } finally {
      inFlight.current = false;
    }
    // Правки во время запроса — следующим сохранением.
    if (!blocked.current && pendingKeys.current.size > 0 && !failed) {
      timer.current = setTimeout(() => void flushRef.current(), delay);
    }
  }, [delay]);
  const lastFailed = useRef(false);
  const flushRef = useRef(flush);
  flushRef.current = flush;

  const change = useCallback(
    (next: V, keys: readonly string[]) => {
      valueRef.current = next;
      setValue(next);
      for (const key of keys) pendingKeys.current.add(key);
      // Без ключей — правка только в окне (например, пустое название, которое сервер не примет).
      if (blocked.current || keys.length === 0) return;
      setStatus("pending");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flushRef.current(), delay);
    },
    [delay],
  );

  const retry = useCallback(() => void flushRef.current(), []);

  const settle = useCallback(async (): Promise<number | null> => {
    // Несколько кругов: правки, сделанные во время запроса, уходят следующим.
    for (let round = 0; round < 50; round++) {
      if (blocked.current) return null;
      if (inFlight.current) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        continue;
      }
      if (pendingKeys.current.size === 0) return versionRef.current;
      await flushRef.current();
      if (lastFailed.current) return null;
    }
    return null;
  }, []);

  const reload = useCallback(async () => {
    const result = await io.current.load();
    pendingKeys.current.clear();
    blocked.current = false;
    versionRef.current = result.part.version;
    valueRef.current = result.value;
    setValue(result.value);
    setConflict(null);
    setError(null);
    setStatus("idle");
    io.current.onSaved?.(result);
  }, []);

  // Уход со страницы — сохраняем то, что успели.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void flushRef.current();
      }
    },
    [],
  );

  return {
    value,
    change,
    status,
    savedAt,
    error,
    conflict,
    dirty: status === "pending" || status === "saving" || status === "error" || status === "conflict",
    retry,
    reload,
    settle,
  };
}
